import { Button, Spacer, Switch } from '@odekoteam/doppio';
import { useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { UPDATE_LOCATIONS_THIRD_PARTY_EXCLUSION } from 'src/graphql/requests/updateLocationsThirdPartyExclusion';
import { GetLocation } from 'src/graphql/types/delivery';
import {
  GUpdateLocationsThirdPartyExclusionMutation,
  GUpdateLocationsThirdPartyExclusionMutationVariables,
} from 'src/graphql/types/generated';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { refreshRouteData } from 'src/state/slices/delivery/getRoutes';
import {
  confirmMetrobiLocations,
  setMetrobiPending,
} from 'src/state/slices/locations';
import { hideModal } from 'src/state/slices/modal';
import { RootState } from 'src/state/store';
import { Flex } from 'theme-ui';
import { useMutation } from 'urql';
import {
  METROBI_INHERITANCE,
  METROBI_TIMING,
  useMetrobiAdmin,
} from '../../LocationsTable/metrobi';
import ExpandableLocationsList from './ExpandableLocationsList';

export default function MetrobiEligibilityTab(): JSX.Element {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const isAdmin = useMetrobiAdmin();
  const variables = useAppSelector((state) => state.modal.variables);
  const contextId: string = variables.metrobiContextId;
  const locations = Array.from(
    new Map<string, GetLocation>(
      (variables.locations as GetLocation[]).map((location) => [
        location.id,
        location,
      ]),
    ).values(),
  );
  const [eligible, setEligible] = useState<boolean>(
    variables.metrobiEligible ?? true,
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string>();
  const submitting = useRef(false);
  const [, update] = useMutation<
    GUpdateLocationsThirdPartyExclusionMutation,
    GUpdateLocationsThirdPartyExclusionMutationVariables
  >(UPDATE_LOCATIONS_THIRD_PARTY_EXCLUSION);

  const handleUpdate = async (): Promise<void> => {
    if (
      submitting.current ||
      !isAdmin ||
      !locations.length ||
      Boolean(store.getState().locations.metrobi?.pendingIds.length) ||
      store.getState().locations.metrobi?.contextId !== contextId
    )
      return;
    submitting.current = true;
    setPending(true);
    setError(undefined);
    const locationIds = locations.map((location) => location.id);
    const requestedIds = new Set(locationIds);
    dispatch(setMetrobiPending({ contextId, ids: locationIds }));
    try {
      const result = await update({
        input: { locationIds, locationExcluded: !eligible },
      });
      if (store.getState().locations.metrobi?.contextId !== contextId) return;
      if (result.error) throw new Error(result.error.message);
      const payload = result.data?.updateLocationsThirdPartyExclusion;
      if (payload?.userErrors?.length)
        throw new Error(
          payload.userErrors.map((item) => item.message).join(' '),
        );
      const returned = payload?.locations;
      const ids = new Set(returned?.map((location) => location.id));
      if (
        !returned ||
        ids.size !== requestedIds.size ||
        returned.length !== locationIds.length ||
        returned.some(
          (location) =>
            !requestedIds.has(location.id) ||
            location.locationExcluded !== !eligible ||
            typeof location.effectivelyExcluded !== 'boolean' ||
            (location.locationExcluded && !location.effectivelyExcluded),
        )
      )
        throw new Error('The server did not confirm every selected location.');
      const offCount = returned.filter(
        (location) => location.effectivelyExcluded,
      ).length;
      dispatch(
        confirmMetrobiLocations({
          contextId,
          locations: returned,
          message: `Updated ${returned.length} ${
            returned.length === 1 ? 'location' : 'locations'
          }: ${returned.length - offCount} On, ${offCount} Off.${
            eligible && offCount
              ? ' Company-excluded locations remain Off.'
              : ''
          }`,
        }),
      );
      dispatch(hideModal());
      // Delivery routes survive navigation to Locations. Refresh both cached
      // projections so returning to the map uses the confirmed eligibility.
      dispatch(refreshRouteData());
    } catch (cause) {
      if (store.getState().locations.metrobi?.contextId === contextId) {
        setError(
          `Metrobi eligibility could not be confirmed. ${
            cause instanceof Error ? cause.message : 'The request failed.'
          } Please retry; the displayed values are the last confirmed state.`,
        );
      }
    } finally {
      submitting.current = false;
      setPending(false);
      dispatch(setMetrobiPending({ contextId, ids: [] }));
    }
  };

  return (
    <>
      <Flex sx={{ alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span sx={{ variant: 'text.paragraphMdDefault' }}>
          Update Metrobi eligible to:
        </span>
        <Switch
          id="metrobi-eligibility-bulk"
          accessibilityLabel="Metrobi Eligible for selected locations"
          isChecked={eligible}
          onCheckedChange={setEligible}
          isDisabled={pending || !isAdmin}
        />
        <span sx={{ variant: 'text.paragraphLgDefault' }}>
          {eligible ? 'ON' : 'OFF'}
        </span>
      </Flex>
      <p sx={{ variant: 'text.paragraphMdDefault' }}>
        You will update {locations.length} selected{' '}
        {locations.length === 1 ? 'location' : 'locations'}.
      </p>
      <p sx={{ variant: 'text.paragraphMdDefault' }}>{METROBI_INHERITANCE}</p>
      <p sx={{ variant: 'text.paragraphMdDefault' }}>{METROBI_TIMING}</p>
      <ExpandableLocationsList locations={locations} />
      {error && <p role="alert">{error}</p>}
      {pending && <p role="status">Saving Metrobi eligibility…</p>}
      <Flex sx={{ justifyContent: 'center', marginTop: '24px' }}>
        <Button
          id="update-metrobi-eligibility"
          variant="secondary"
          onPress={handleUpdate}
          isDisabled={pending || !isAdmin || !locations.length}
        >
          {error ? 'Retry update' : 'Update'}
        </Button>
        <Spacer width="$1" />
        <Button
          id="cancel-metrobi-eligibility"
          variant="ghost"
          isDisabled={pending}
          onPress={(): void => {
            dispatch(hideModal());
          }}
        >
          Cancel
        </Button>
      </Flex>
    </>
  );
}
